import { LocalizedString } from '@onrunning/backstage-models';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { globalConstants } from '../constants/global.constants';
import { useUserStore } from '../stores/user';
import { isString } from './is-string';

export function useFormat() {
  const { userLocale } = storeToRefs(useUserStore());

  function formatTimeForTwoDigits(time: number): string {
    return time.toString().padStart(2, '0');
  }

  function formatDateToLocaleStringIgnoringTime(date: Date | string) {
    return formatDate(moment(date).format(globalConstants.dateFormat), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  function formatNotificationDate(dateString: string) {
    const date = new Date(dateString);

    return new Intl.DateTimeFormat(userLocale.value, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }).format(date);
  }

  function formatDate(
    inputDate: Date | string,
    options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    },
  ) {
    const date = isString(inputDate) ? moment(inputDate).toDate() : inputDate;

    if (date && Object.prototype.toString.call(date) === '[object Date]') {
      if (!isNaN(date.getTime())) {
        return new Intl.DateTimeFormat(userLocale.value, options).format(date);
      }
    }

    return '';
  }

  function formatDateNoUTC(date: Date | string) {
    return formatDate(date, { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function formatCurrency(currencyCode: string | undefined, value: any, ignoreZero = true) {
    if (!currencyCode) {
      return '';
    }

    if (value || (value === 0 && !ignoreZero)) {
      try {
        return new Intl.NumberFormat(userLocale.value, {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'code',
          currencySign: 'accounting',
        } as any).format(value);
      } catch (err) {
        // default fallback format for buggy safari 14.1.x users
        return `${currencyCode} ${value}`;
      }
    }

    return '';
  }

  function currencyComparator(a: number, b: number) {
    // We want currencies to be sorted by value, disregarding
    // currency. The `|| 0` is for undefined and NaN values, of which
    // for some reason we have some.
    return (a || 0) - (b || 0);
  }

  function formatDateToIsoStringIgnoringTime(date: Date | string) {
    let dateFormat = globalConstants.dateFormat;

    if (isString(date)) {
      if (moment(date).isValid()) {
        return moment(date).format(dateFormat);
      }

      moment.locale(userLocale.value);
      dateFormat = globalConstants.dateLocalFormat;
    }

    const formattedDate = moment(date, dateFormat).format(globalConstants.dateFormat);

    return formattedDate;
  }

  function formatNumber(value: any) {
    if (!value) {
      return '-';
    }

    return value;
  }

  function formatPercentage(value: number, ignoreZero = false) {
    if (value || (value === 0 && !ignoreZero)) {
      return `${(value * 100).toFixed(2)}%`;
    }

    return '';
  }

  function gridNumberFormatter(params: any): string {
    return formatNumber(params.value).toString();
  }

  function gridDateFormatter(params: any): string {
    return formatDateNoUTC(params.value).toString();
  }

  function getTranslationFromTranslateField(localizedString: LocalizedString): string {
    const empty = '-';

    return (
      (localizedString as any)[userLocale.value] ?? (localizedString as any)[globalConstants.fallBackLocale] ?? empty
    );
  }

  function launchCalendarDateFormatter(date: string): string {
    return formatDate(date, { day: 'numeric', month: 'short' });
  }

  function formatGridNumberColumn(params: any): string {
    const parsedNumber = parseInt(params.value, 10);

    return isNaN(parsedNumber) ? '-' : parsedNumber.toString();
  }

  function formatNonLocalisedDateString(date: Date | string) {
    return moment(date).format(globalConstants.dateFormat);
  }

  function parseAndFormatTimestampInUtc(timestamp: Date | string) {
    return moment.utc(timestamp).format(globalConstants.dateFormat);
  }

  /**
   * Parse a UTC date to the same date in local timezone.
   * Example "2021-09-02T00:00:00.000Z" will be parsed to "2021-09-02" in the local timezone.
   * @param date Date to parse, probably in UTC
   * @returns Formatted date in local timezone with the correct locale format
   */
  function parseUtcDateToLocaleDate(date: Date | string) {
    return formatDateNoUTC(parseAndFormatTimestampInUtc(date));
  }

  const getFormattedCountryCode = (countryCode: string, countryName: string): string => {
    return `${countryCode}${globalConstants.countryCodeAndCountryDelimiter}${countryName}`;
  };

  const getSplittedCountryCode = (formattedCountryCode: string) => {
    return formattedCountryCode ? formattedCountryCode.split(globalConstants.countryCodeAndCountryDelimiter) : [];
  };

  return {
    formatTimeForTwoDigits,
    formatCurrency,
    currencyComparator,
    formatDate,
    formatDateNoUTC,
    formatDateToIsoStringIgnoringTime,
    formatDateToLocaleStringIgnoringTime,
    formatNotificationDate,
    formatNumber,
    formatPercentage,
    gridNumberFormatter,
    gridDateFormatter,
    getTranslationFromTranslateField,
    launchCalendarDateFormatter,
    formatGridNumberColumn,
    formatNonLocalisedDateString,
    parseUtcDateToLocaleDate,
    getFormattedCountryCode,
    getSplittedCountryCode,
  };
}
